// 🔹 React Core
import React, { useEffect, useState } from "react";

// 🔹 State Management
import { RootState, useAppDispatch, useAppSelector } from "../../../store";

// 🔹 UI Component Libraries
import { Spin } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";

// 🔹 Internal Components
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import EditSummaryDetailsModal from "./Components/Modals/EditSummaryDetailsModal";

// 🔹 Internal Utilities
import {
  CapitalizeFirstWord,
  formatToLocalDate,
  useDebounce,
} from "../../../helpers/utils";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import { decryptByText } from "../../../helpers/bycrypt";

// 🔹 Redux Actions
import { GetSummaryDetails } from "../../Redux/summaryDetails";

// 🔹 Assets
import { svg } from "../../../Assets/svg";

type MetaUser = {
  first_name?: string;
  last_name?: string;
};

type MetaType = {
  by?: MetaUser | null;
  on?: string | null;
};

type QuotationCardProps = {
  title: string;
  html: string;

  onEdit: () => void;
  meta?: MetaType | null;
  canEdit?: boolean;
};

export const QuotationCard: React.FC<QuotationCardProps> = ({
  title,
  html,
  onEdit,
  meta,
  canEdit,
}) => {
  const isNameBy = meta?.by?.first_name || meta?.by?.last_name || meta?.on;
  const firstName = meta?.by?.first_name
    ? CapitalizeFirstWord(decryptByText(meta.by.first_name))
    : "";
  const lastName = meta?.by?.last_name
    ? CapitalizeFirstWord(decryptByText(meta.by.last_name))
    : "";
  const name =
    meta?.by?.first_name || meta?.by?.last_name
      ? `${firstName} ${lastName}`.trim()
      : "-";
  const on = formatToLocalDate(meta?.on, true);
  return (
    <div className="card border shadow-sm rounded-3 bg-white p-3 mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
        <div className="flex-grow-1">
          <div className="row">
            <div className="col-12 col-md-9">
              <h6 className="fs-6 fw-bold text-black mb-0">{title}</h6>
            </div>

            <div className="col-12 col-md-3">
              <h6 className="fs-7 fw-normal text-secondary mb-0">
                {isNameBy ? `${name || "-"} on ${on || "-"}` : `-`}
              </h6>
            </div>
          </div>
        </div>

        {canEdit && (
          <button
            type="button"
            className="btn btn-link p-0 svg-32 ms-3"
            onClick={onEdit}
          >
            {svg?.editBtn}
          </button>
        )}
      </div>

      {/* Body */}
      <div
        className="ck-read-editor"
        dangerouslySetInnerHTML={{ __html: html || "-" }}
      />
    </div>
  );
};

const SummaryDetails = () => {
  // Use
  const dispatch = useAppDispatch();

  // Selector
  const { SummaryDetailsList = [], SummaryDetailCount } = useAppSelector(
    (state: RootState) => state?.summaryDetail,
  );

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );

  const { getSummaryDetailsLoading, authLoading } = useAppSelector(
    (state: RootState) => state?.loading,
  );

  const [editSummaryDetailsModal, setEditSummaryDetailsModal] = useState({
    open: false,
    data: null,
    type: "",
  });

  // Params State
  const limit = 25;
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const debouncedSearch = useDebounce(search, 0);
  const payload = { search: search, page: currentPage, limit };

  useEffect(() => {
    dispatch(GetSummaryDetails(payload, limit, setHasMore));
  }, [currentPage, debouncedSearch]);

  if (getSummaryDetailsLoading && !SummaryDetailsList) {
    return <FullScreenLoader />;
  }

  return USER_PERMISSIONS?.READ ? (
    <div className="page-content h-100 px-40 py-40">
      <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-sm-center align-items-start mb-md-4 mb-3">
        <h2 className="text-black fs-36 fw-bolder page-title mb-0">
          <span>Summary Details</span>{" "}
          {getSummaryDetailsLoading && currentPage === 1
            ? ""
            : SummaryDetailCount
              ? `(${SummaryDetailCount})`
              : ""}
        </h2>
        <div
          className={`pageSearch mw-100 position-relative d-inline-block order-sm-2 order-3 ms-auto`}
        >
          <input
            type="text"
            value={search}
            className="form-control form-control-lg"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
          />
          {search?.length ? (
            <span className="cursor-pointer" onClick={() => setSearch("")}>
              {svg?.searchClear}
            </span>
          ) : (
            <span>{svg?.search}</span>
          )}
        </div>
        <div className="d-flex gap-sm-3 gap-2 order-sm-3 order-2">
          <NotificationButton />
        </div>
      </div>

      <div className="card page-table-card border-0 shadow rounded-4 flex-fill overflow-y-auto">
        <div
          className="card-body overflow-y-auto p-md-4 p-3"
          id="scrollableDiv"
        >
          <InfiniteScroll
            dataLength={SummaryDetailsList?.length}
            next={() => setCurrentPage((prevPage) => prevPage + 1)}
            hasMore={hasMore}
            loader={
              currentPage > 1 && (
                <div className="text-center p-2">
                  <Spin />
                </div>
              )
            }
            scrollableTarget="scrollableDiv"
          >
            {getSummaryDetailsLoading && currentPage === 1 ? (
              <FullScreenLoader />
            ) : SummaryDetailsList?.length > 0 ? (
              SummaryDetailsList.map((item: any, index: number) => {
                return (
                  <div key={item?._id || index} className="mb-3 mb-md-4">
                    {/* Title */}
                    <h5 className="fs-5 fw-bold text-black mb-2">
                      {item?.name || item?.category?.name}
                    </h5>

                    {/* UI Card */}
                    {/* <QuotationCard
                      title="Paragraph Quotation UI"
                      html={item?.quoteData}
                      meta={item?.quoteData_meta}
                      canEdit={USER_PERMISSIONS?.MODIFY}
                      onEdit={() =>
                        setEditSummaryDetailsModal({
                          open: true,
                          data: item,
                          type: "ui",
                        })
                      }
                    /> */}

                    {/* PDF Card */}
                    <QuotationCard
                      title="Paragraph Quotation PDF"
                      html={item?.pdfData}
                      meta={item?.pdfData_meta}
                      canEdit={USER_PERMISSIONS?.MODIFY}
                      onEdit={() =>
                        setEditSummaryDetailsModal({
                          open: true,
                          data: item,
                          type: "pdf",
                        })
                      }
                    />
                  </div>
                );
              })
            ) : (
              <NoDataMessage
                icon={svg?.noSummaryDetails}
                text="You do not have any summary details yet."
              />
            )}
          </InfiniteScroll>
        </div>
      </div>

      {editSummaryDetailsModal?.open && (
        <EditSummaryDetailsModal
          onSuccess={() =>
            setEditSummaryDetailsModal({ open: false, data: null, type: "" })
          }
          options={editSummaryDetailsModal}
          setState={() =>
            setEditSummaryDetailsModal({ open: false, data: null, type: "" })
          }
        />
      )}
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default SummaryDetails;
